import {
  EMPLOYMENT_BONDS,
  commissionWarnings,
  employmentBondLabels,
  type CommissionWarning,
  type EmploymentBond,
} from '@hue/contracts';
import { Button, Modal, Select, Switch } from '@hue/design-system';
import { useState, type ReactNode } from 'react';
import { commissionAccountLabel } from './commission-account-label.js';

export interface CommissionProfileValue {
  receivesCommission: boolean;
  attendsSchedule: boolean;
  employmentBond: EmploymentBond | '';
  commissionOffPayroll: boolean;
}

const PAYROLL_OPTIONS = [
  { value: 'IN', label: 'Na folha' },
  { value: 'OUT', label: 'Fora da folha' },
];

/// As marcações e o Vínculo de trabalho da ficha do profissional (ADR 0082, decisões 2 e 21 a 23). A
/// escolha que contraria a lei abre um aviso, e a pessoa decide: o aviso informa e nunca impede.
export function CommissionProfileFields({
  value,
  onChange,
  readOnly = false,
  error,
  advances,
}: {
  value: CommissionProfileValue;
  onChange: (next: CommissionProfileValue) => void;
  readOnly?: boolean;
  error?: string | undefined;
  /// Vales do profissional já salvo (ADR 0082, decisão 24).
  advances?: ReactNode;
}) {
  const [pending, setPending] = useState<{
    warning: CommissionWarning;
    next: CommissionProfileValue;
  } | null>(null);

  function chooseBond(bond: string) {
    const next = {
      ...value,
      employmentBond: bond as EmploymentBond,
      commissionOffPayroll: false,
    };
    if (bond === 'INFORMAL' && value.employmentBond !== 'INFORMAL')
      setPending({ warning: commissionWarnings.INFORMAL, next });
    else onChange(next);
  }
  function choosePayroll(payroll: string) {
    const next = { ...value, commissionOffPayroll: payroll === 'OUT' };
    if (payroll === 'OUT' && !value.commissionOffPayroll)
      setPending({ warning: commissionWarnings.OFF_PAYROLL, next });
    else onChange(next);
  }

  return (
    <div className="grid gap-3 sm:col-span-2">
      <Switch
        checked={value.attendsSchedule}
        disabled={readOnly}
        onChange={(attendsSchedule) => onChange({ ...value, attendsSchedule })}
      >
        Atende na agenda
      </Switch>
      {value.attendsSchedule ? null : (
        <p className="text-xs text-content-faint">
          Fica fora da Agenda e do portal, mas pode receber comissão por vendas.
        </p>
      )}
      <Switch
        checked={value.receivesCommission}
        disabled={readOnly}
        onChange={(receivesCommission) => onChange({ ...value, receivesCommission })}
      >
        Recebe comissão
      </Switch>
      {value.receivesCommission ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label="Vínculo de trabalho"
            value={value.employmentBond}
            options={[
              { value: '', label: 'Escolha o vínculo' },
              ...EMPLOYMENT_BONDS.map((bond) => ({
                value: bond,
                label: employmentBondLabels[bond],
              })),
            ]}
            onValueChange={chooseBond}
            disabled={readOnly}
            error={error}
          />
          {value.employmentBond === 'CLT' ? (
            <Select
              label="Pagamento da comissão"
              value={value.commissionOffPayroll ? 'OUT' : 'IN'}
              options={PAYROLL_OPTIONS}
              onValueChange={choosePayroll}
              disabled={readOnly}
            />
          ) : null}
          {value.employmentBond ? (
            <p className="text-xs text-content-faint sm:col-span-2">
              Conta do plano:{' '}
              {commissionAccountLabel(value.employmentBond, value.commissionOffPayroll)}
            </p>
          ) : null}
          {advances}
        </div>
      ) : (
        <p className="text-xs text-content-faint">
          Quem não recebe comissão não aparece na lista de comissões da venda.
        </p>
      )}
      <Modal
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
        title="Antes de confirmar esta escolha"
        footer={
          <>
            <Button type="button" variant="outline" onClick={() => setPending(null)}>
              Voltar
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (pending) onChange(pending.next);
                setPending(null);
              }}
            >
              Entendi, manter a escolha
            </Button>
          </>
        }
      >
        {pending ? (
          <div className="grid gap-3 text-sm">
            <div className="grid gap-2 rounded-lg bg-warning/10 p-3">
              <p className="font-semibold">{pending.warning.title}</p>
              {pending.warning.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
            <p className="text-xs text-content-faint">{pending.warning.note}</p>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
